import Debug from 'debug';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { Util } from '../util/util';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { User } from '../types/user';
import { Note } from '../types/note';
import { Bank, TransferMoney, UpdateBank } from '../types/bank';

import { setAuthToken } from '../../interceptors/api.interceptor';

import { CacheService } from '../services/cache_service';

import { map, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

import * as Sentry from '@sentry/browser';
import { EnvironmentService } from './environment_service';

const debug = Debug('notd:shared:UserService');

@Injectable({ providedIn: 'root' })
export class UserService {
  private platformId = inject(PLATFORM_ID);
  private document = inject(DOCUMENT);
  private http = inject(HttpClient);
  private cacheService = inject(CacheService);
  private utilities = inject(Util);
  private environmentService = inject(EnvironmentService);

  redirect_uri: string = '';
  curUserPromise: Promise<User>;
  curUser: User;

  private _curUserSource = new BehaviorSubject<User>(undefined);

  curUser$ = this._curUserSource.asObservable();

  constructor() {
    debug('UserService constructor');

  }

  getProfile(forceRefresh?: boolean): Promise<User> {
    if (isPlatformServer(this.platformId)) {
      return Promise.resolve(null);
    }

    if (this.isLoggedIn() && !forceRefresh) {
      return Promise.resolve(this.curUser);
    } else if (!this.curUserPromise || forceRefresh) {
      this.curUserPromise = this.getProfileOnly().then((curUser) => {
        return curUser;
      }, () => this.runOAuth().then((response) => {
        const authorization = response.headers.get('Notd-Auth');
        debug('getProfile runOAuth authorization', authorization);
        setAuthToken(authorization, false);

        if (isPlatformBrowser(this.platformId)) {
          if ((<any>window).dataLayer) {
            (<any>window).dataLayer.push({
              event: 'login',
            });
          }
        }
        return this.getProfileOnly();
      }, (result: any) => {
        if (result.status === 403) {
          debug('Not Authenticated', result);
          this.curUser = <User>{
            id: ''
          };
          this.cacheService.clearUserCache();
          this.cacheService.clearCache();
          Sentry.configureScope((scope) => scope.setUser({}));
          this._curUserSource.next(this.curUser);
          return this.curUser;
        }

        console.error('Error:', result);
        return null;
      })
        .catch(err => {
          throw err;
        }))
        .catch(err => {
          throw err;
        });
    }

    return this.curUserPromise;
  }

  public getUserProfile(userId: string | number, force = false): Promise<User> {
    const cache = this.cacheService.getUserProfileCache(userId);

    if (isPlatformServer(this.platformId)) {
      return Promise.resolve(null);
    }

    if (cache && !force) {
      return Promise.resolve(cache);
    } else {
      const url = `${this.environmentService.apiServerBase}/api/user/${userId}/profile`;

      return fetch(url, { credentials: 'include' })
        .then(response => {
          const headers = response.headers;
          const authorization = headers.get('x-notd-auth');

          if (authorization) {
            setAuthToken(authorization, false);
          }

          if (!response.ok) {
            throw new Error('Network response was not ok');
          }

          return response.json();
        })
        .then(user => {
          const typedUser: User = user as User;
          this.cacheService.setUserProfileCache(typedUser);
          debug('getUserProfile user', typedUser);
          return typedUser;
        })
        .catch(err => {
          console.error('Error retrieving user profile', err);

          if (this.curUser && this.curUser.id === userId) {
            this.curUser = <User>{
              id: ''
            };

            this.cacheService.clearUserCache();
            this.cacheService.clearCache();
            this._curUserSource.next(this.curUser);
          }
          throw err;
        });
    }
  }

  public getUserSubscriptionList(): Promise<any> {
    return this.http.get('/api/user/subscriptions/list')
      .toPromise().catch((err) => {
        console.error('Error retrieving user subscription list', err);
      });
  }

  getUserCommentsList(userId: any): Promise<Comment[] | object | void> {
    return this.http.get(`/api/user/${userId}/posts/comments`)
      .toPromise().catch((err) => {
        console.error('Error retrieving user commetns list', err);
      });
  }

  getUserRatingsList(userId: any): Promise<any> {
    return this.http.get(`/api/user/${userId}/posts/ratings`)
      .toPromise().catch((err) => {
        console.error('Error retrieving user ratings list', err);
      });
  }

  getActivityList(fromTime: number): Observable<any> {
    let url = '/api/user/purchases';

    if (fromTime) {
      url += '?after=' + fromTime;
    }

    return this.http.get(url);
  }

  getBankList(): Promise<Bank[] | object | void> {
    return this.http.get('/api/user/profile/bank/list')
      .toPromise().catch((err) => {
        console.error('Error retrieving bank list', err);
      });
  }

  public getUserNotes(userIdent: number | string): Promise<Note[] | object | void> {
    return this.http.get(`/api/user/${userIdent}/posts/list`)
      .toPromise();
  }

  putUserName(user: any) {
    this.cacheService.removeUserProfileCache(user);

    if (user.username) {
      this.cacheService.removeUserProfileByUsernameCache(user.username);
    }

    return this.http.put('/api/user/profile/username/' + user.userName, {})
      .pipe(
        map((resp: any) => {
          this.cacheService.setUserProfileCache(user);
          this.curUser = resp;
          return resp;
        })
      )
      .toPromise();
  }

  updateUser(newUserInfo: User, localOnly?: boolean) {
    const payload = Object.assign(Object.assign({}, this.curUser), newUserInfo);

    if (!localOnly) {
      return this.http.post('/api/user/profile', payload, {})
        .toPromise().then((response: any) => {
          if (response.profileImageUrl) {
            response.profileImageUrl = this.utilities.uncacheUrl(response.profileImageUrl);
          }

          this.curUser = response;
          this._curUserSource.next(this.curUser);

          return <User>this.curUser;
        },
          err => {
            throw err;
          });
    } else {
      Object.assign(this.curUser, newUserInfo);
      this._curUserSource.next(this.curUser);
    }
  }

  uploadUserAvatar(fileToUpload: File): Promise<any> {
    const formData: FormData = new FormData();

    formData.append('file', fileToUpload, 'avatar.png');

    return this.http.post<any>(`/api/user/profile/image`, formData).toPromise();
  }

  isLoggedIn(): boolean {

    if (isPlatformServer(this.platformId)) {
      return false;
    }

    if (this.curUser === undefined) {
      return undefined;
    }
    return this.curUser.id !== '';
  }

  login(register?: boolean, customRedirectUrl?: string): Promise<any> {
    if (isPlatformServer(this.platformId)) {
      return Promise.resolve(null);
    }

    return this.http.get('/api/oauth/state')
      .toPromise()
      .then((response: any) => {
        debug('got Oauth state', response);
        console.log('got Oauth state', response);

        const params: URLSearchParams = new URLSearchParams();
        const transactUrl = this.environmentService.transactBaseUrl + '/oauth2';

        if (!response['client_id'] || response['client_id'].length < 1) {
          console.error('INVALID OAUTH response:', response);
          return;
        }
        const maxUrlLength = 200;
        params.set('response_type', 'code');
        params.set('client_id', response['client_id']);
        params.set('state', response['state']);
        params.set('register', (register) ? '1' : '');
        params.set('scope', 'email,name');

        if (isPlatformBrowser(this.platformId)) {
          const docLocation = this.document.location;

          if (customRedirectUrl) {
            params
              .set(
                'redirect_uri',
                `${docLocation.origin}/${customRedirectUrl}`);
          } else {
            params.set('redirect_uri', this.redirect_uri ?
              this.redirect_uri.substring(0, maxUrlLength) : docLocation.href.substring(0, maxUrlLength));
          }
        }

        debug('params', params);
        debug('URL: ' + params.toString());

        if (isPlatformBrowser(this.platformId)) {
          this.document.location.assign(transactUrl + '?' + params.toString());
        }
      })
      .catch(err => {
        console.error('Error ', err);
      });
  }

  logout(): Promise<any> {
    return this.http.get('/api/user/logout')
      .toPromise().then(() => {
        this.curUser = <User>{
          id: ''
        };
        this.cacheService.clearUserCache();
        this.cacheService.clearCache();
        this._curUserSource.next(this.curUser);
        this.curUserPromise = null;
        setAuthToken('');
      }, (err) => {
        console.error(err);
      });
  }

  runOAuth(): Promise<any> {
    if (this.isLoggedIn()) {
      return Promise.resolve(null);
    }
    const code = this.getParameterByName('code');
    const state = this.getParameterByName('state');

    if (!code || code.length < 10) {
      return Promise.reject({ status: 403 });
    }
    if (!state || state.length < 10) {
      return Promise.reject({ status: 403 });
    }

    let redirectUri = '';
    if (isPlatformBrowser(this.platformId)) {
      redirectUri = this.document.location.origin + this.document.location.pathname;
    }

    const data = {
      state,
      code,
      redirect_uri: redirectUri,
    };


    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      observe: 'response' as const,
    };


    return this.http.post('/api/oauth/code/token', data, httpOptions)
      .toPromise();
  }

  setLocationAddress(address: string): Promise<any> {
    return this.http.post(`/api/user/location/address/${address}`, {})
      .toPromise()
      .catch(err => console.error('Error while saving user location', err.error || err));
  }

  setLocationCords(lat: number, lng: number): Promise<any> {
    const params = new HttpParams()
      .set('lat', lat.toString())
      .set('lng', lng.toString());

    return this.http.post(`/api/user/location/address/lat_lng`, params)
      .toPromise()
      .catch(err => console.error('Error while saving user location', err.error || err));
  }

  listBanks(): Promise<any> {
    return this.http.get('/api/user/profile/bank/list', {}).toPromise();
  }

  putUserPreference(key: string, value: string | any): Promise<User | object> {
    return this.http.put('/api/user/profile/preferences/' + key + '/' + value, {})
      .toPromise()
      .catch(err => {
        console.error('Error setting user preferences', err);
        throw err;
      });
  }

  getSavedNotes(): Observable<any> {
    return this.http.get('/api/user/saved-posts/list', {});
  }

  getUserImagePath(): string {
    return '/api/user/profile/image';
  }

  getTaxDocPath(): string {
    return '/api/user/profile/legal/tax_document';
  }

  getIdentityDocPath(): string {
    return '/api/user/profile/legal/identity_document';
  }

  updateBank(params: UpdateBank): Promise<any> {
    return this.http.post('/api/user/profile/bank/info', params).toPromise();
  }

  updateLegalInfo(params: any): Promise<any> {
    return this.http.post('/api/user/profile/legal/info', params).toPromise();
  }

  transferMoney(params: TransferMoney): Promise<any> {
    return this.http.post('/api/user/payout/request/bank', params).toPromise();
  }

  getBankFee(bankId: string): Promise<any> {
    return this.http.get('/api/user/profile/bank/' + bankId + '/transfer/fee', {}).toPromise();
  }

  listCommunities(): Observable<any> {
    return this.http.get('/api/user/channel/member/list', {})
      .pipe(
        catchError(err => {
          throw err;
        })
      );
  }

  public getBlockedUsers(streamId: number | string): Promise<any> {
    return this.http.get(`/api/channel/${streamId}/members/blocked/list`).toPromise();
  }

  public blockCommentUser(streamId: number | string, userId: number | string, comments: string): Promise<any> {
    const params: HttpParams = new HttpParams()
      .set('comments', comments);
    return this.http.put(`/api/channel/${streamId}/member/block/user/${userId}`, params).toPromise();
  }

  public removeBlockedUser(streamId: number | string, userId: number | string): Promise<any> {
    return this.http.delete(`/api/channel/${streamId}/member/block/user/${userId}`).toPromise();
  }

  public deleteUser(): Promise<any> {
    return this.http.delete(`/api/user/profile/delete/start`).toPromise();
  }

  public confirmUserRemoval(token: string | number): Promise<any> {
    return this.http.put(`/api/user/profile/delete/confirm/${token}`, {}).toPromise();
  }

  public getParameterByName(name, url?) {
    if (isPlatformBrowser(this.platformId)) {
      if (!url) {
        url = this.document.location.href;
      }
    } else {
      return null;
    }

    name = name.replace(/[\[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
    const results = regex.exec(url);
    if (!results) {
      return null;
    }
    if (!results[2]) {
      return '';
    }
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }

  public getUserProfileByUsername(username: string): Promise<User> {
    const cachedProfile = this.cacheService.getUserProfileByUsernameCache(username);

    if (cachedProfile) {
      return Promise.resolve(cachedProfile);
    } else {
      return this.http.get<User>(`/api/user/${username}/profile`, {})
        .pipe(
          map((user: User) => {
            this.cacheService.setUserProfileByUsernameCache(user);
            return user;
          })
        )
        .toPromise()
        .catch(error => {
          console.error('Error while getting user profile');
          throw error;
        })
    }
  }

  public getProfileOnly(): Promise<User> {
    const httpOptions = {
      withCredentials: true
    };

    return this.http.get('/api/user/profile', httpOptions)
      .toPromise().then((response: User) => {

        if (response.profileImageUrl) {
          response.profileImageUrl = this.utilities.uncacheUrl(response.profileImageUrl);
        }

        this.curUser = response;

        if (isPlatformBrowser(this.platformId)) {
          Sentry.configureScope((scope) => {
            scope.setUser({
              email: this.curUser.email1,
              id: this.curUser.id + ''
            });
          });
        }

        this._curUserSource.next(this.curUser);
        return <User>this.curUser;
      }, (err) => {
        if (isPlatformBrowser(this.platformId)) {
          Sentry.configureScope((scope) => scope.setUser({}));
        }
        throw err;
      })
      .catch(err => {
        console.log(err.error || err);
        if (isPlatformBrowser(this.platformId)) {
          Sentry.configureScope((scope) => scope.setUser({}));
        }
        throw err;
      });
  }

  public getUserSales(userId: number | string): Promise<Record<string, unknown> | object> {
    return this.http.get<Record<string, unknown>>(`/api/user/${userId}/sales`)
      .toPromise();
  }
}